import React from 'react';
import PropTypes from 'prop-types';

import {Button} from '@stubhub/bamboo';
import FormattedLink from '@stubhub/react-formatted-link';
import GeoFilter from '@stubhub/react-geo-filter';
import LocalizationPicker from '@stubhub/react-localization-picker';
import Logo, {BRAND_TYPES as LOGO_BRANDS, LOGO_TYPES} from '@stubhub/react-logo';
import ModalMask from '@stubhub/react-modal-mask';
import SlideWrapper from '@stubhub/react-slide-wrapper';
import {ssr_wait, connect} from '@stubhub/react-store-provider';

import {formatMenu, pullMenuByLevel} from './helpers/multi-layer-nav';
import controller from './nav-controller';
import NavMenuOptionList from './nav-menu-option-list';
import NavMultiLayerMenu from './nav-multi-layer-menu';
import NavSubCatList from './nav-sub-category-list';

import '../styles/nav-menu.scss';

const menuTypes = ['sports', 'concerts', 'theater-comedy', 'cities'];

export const getDiscoveryMenu = (menuItems) => {
  if (menuItems && menuItems.discovery) {
    const {discovery} = menuItems;
    const formattedDiscovery = formatMenu(discovery);
    const discoveryMenu = {};
    formattedDiscovery.forEach((menuItem) => {
      discoveryMenu[menuItem.id] = menuItem;
    });

    return discoveryMenu;
  }

  return null;
};

const ModalMaskCmp = ModalMask;

class NavMenu extends React.Component {
  static contextTypes = {
    globalRegistry: PropTypes.object,
  };

  constructor(props, context) {
    const {active, activeMenuItem} = props;

    super(props);

    this.state = {
      active,
      activeMenuItem,
      selectedItem: {
        id: null,
        label: null,
        parent: null,
      },
    };

    const {globalRegistry} = context;
    if (globalRegistry && typeof globalRegistry.getFeatureValue === 'function') {
      this.enableSubCatNav = globalRegistry.getFeatureValue('common.enableSubCatNav');
      this.exposeExpandedNavMenu = globalRegistry.getFeatureValue('common.exposeExpandedNavMenu');
      this.debugSubMenuNav = globalRegistry.getFeatureValue('common.debugSubMenuNav');
      this.exposeNavHTML = globalRegistry.getFeatureValue('common.exposeNavHTML');
      this.useAltBrand = globalRegistry.getFeatureValue('common.useAltBrand');
    }
  }

  @ssr_wait
  init() {
    //
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {active: currentActive, activeMenuItem: currentActiveMenuItem} = this.props;
    const {active: newActive, activeMenuItem: newActiveMenuItem} = nextProps;
    if (currentActive !== !!newActive || currentActiveMenuItem !== newActiveMenuItem) {
      this.setState({
        active: !!newActive,
        activeMenuItem: newActiveMenuItem,
      });
    }
  }

  UNSAFE_componentWillMount() {
    this.exposeExpandedNavMenu && this.init();
  }

  /*
   * Invoked when a menu link is clicked
   * Should be attached to an onClick event to properly trigger
   */
  onItemClick = () => {
    const {onItemClick, id, items, webURI} = this.props;
    if (typeof onItemClick === 'function') {
      onItemClick(id, items, webURI);
    }
  };

  /*
   * Invoked when a menu option is clicked
   * Should be attached to an onClick event to properly trigger
   */
  onOptionClick = (evt) => {
    const {onOptionClick, subMenu = []} = this.props;
    let key = '';
    let categoryId = '';

    if (evt && evt.target) {
      if (evt.target.dataset) {
        key = evt.target.dataset.itemId || evt.target.dataset.index;
        categoryId = evt.target.dataset.id || '';
      }
    }

    if (typeof onOptionClick === 'function') {
      if (this.exposeExpandedNavMenu) {
        const item = subMenu.find((e) => {
          return e.id === categoryId;
        });
        if (item && item !== '') {
          key = (item && item.name) || categoryId;
        }
      }
      onOptionClick(key);
      this.clearSelected();
    }
  };

  /*
   * Invoked when user clicks outside of the menu option list
   */
  onClickOutside = () => {
    const {onClickOutside, id} = this.props;

    this.setState({activeItem: ''});
    if (typeof onClickOutside === 'function') {
      onClickOutside(id);
      this.clearSelected();
    }
  };

  onMenuItemClick = ({id, label, parent = null}) => {
    this.setState(() => ({
      selectedItem: {
        id,
        label,
        parent,
      },
    }));
  };

  /*
   * Invoked when user hovers over a menu option or category sub menu option
   */
  onItemHover = () => {
    const {onMenuHover, onCategoryHover, id, items} = this.props;
    if (items) {
      if (typeof onMenuHover === 'function') {
        onMenuHover(id, items);
      }
    } else if (typeof onCategoryHover === 'function') {
      onCategoryHover(id);
    }
  };

  onMenuHover = (id, items) => {
    const {onMenuHover, id: key, type, subMenu} = this.props;

    if (typeof onMenuHover === 'function') {
      if (items) {
        onMenuHover(id, items);
      } else {
        type === 'subMenu' && onMenuHover(key, subMenu);
        this.setState({activeItem: id});
      }
    }
  };

  clearSelected = () => {
    this.setState(() => ({
      selectedItem: {
        id: null,
        label: null,
        parent: null,
      },
    }));
  };

  // 2-layer accordion Menu
  renderVerticalMenu = (items, href) => (
    <>
      <NavMenuOptionList items={items[0].items} href={href} onOptionClick={this.onOptionClick} />
      <NavMenuOptionList items={items[1].items} href={href} onOptionClick={this.onOptionClick} />
      <NavMenuOptionList items={items[2].items} href={href} onOptionClick={this.onOptionClick} />
    </>
  );

  // Multi-layer horizontal nav menu
  renderHorizontalMenu = (items) => {
    const {multiLayeredMenu} = this.props;
    const formattedDiscoveryMenu = getDiscoveryMenu(multiLayeredMenu);
    const menuCategoryItems = !formattedDiscoveryMenu
      ? items[1].items
      : items[1].items.map((menuItem) =>
          Object.prototype.hasOwnProperty.call(formattedDiscoveryMenu, menuItem.id)
            ? formattedDiscoveryMenu[menuItem.id]
            : menuItem
        );

    return (
      <>
        <NavMultiLayerMenu
          items={items[0].items}
          onMenuItemClick={this.onMenuItemClick}
          onOptionClick={this.onOptionClick}
          menuId="root"
        />
        <NavMultiLayerMenu
          items={menuCategoryItems}
          onMenuItemClick={this.onMenuItemClick}
          onOptionClick={this.onOptionClick}
          menuId="root"
        />
        <NavMultiLayerMenu
          items={items[2].items}
          onMenuItemClick={this.onMenuItemClick}
          onOptionClick={this.onOptionClick}
          className="help-menu"
          menuId="root"
        />
      </>
    );
  };

  renderLayeredMenu = (items, selectedItem) => {
    const {multiLayeredMenu} = this.props;
    const formattedDiscoveryMenu = getDiscoveryMenu(multiLayeredMenu) || {};
    let applicableItems = [...items[0].items, ...items[1].items];
    applicableItems = applicableItems.map((menuItem) =>
      Object.prototype.hasOwnProperty.call(formattedDiscoveryMenu, menuItem.id)
        ? formattedDiscoveryMenu[menuItem.id]
        : menuItem
    );
    const menuByLevel = pullMenuByLevel(applicableItems, []);
    const prevItem = !selectedItem.parent
      ? {id: null, label: null, parent: null}
      : [...applicableItems, ...menuByLevel.flat()]
          .filter((item) => item.items || item.subMenu)
          .find((menu) => menu.id === selectedItem.parent);
    const activeClass = selectedItem.id ? 'active' : 'inactive';
    const directionClass = activeClass === 'inactive' ? '' : menuTypes.includes(selectedItem.parent) ? 'left' : 'right';

    return (
      <div className={`mh__nav-menu-options sub ${activeClass}`}>
        {menuByLevel.map((menuItems, idx) => (
          <NavMultiLayerMenu
            key={idx}
            items={menuItems}
            menuId={selectedItem.id}
            prevItem={prevItem}
            onMenuItemClick={this.onMenuItemClick}
            onOptionClick={this.onOptionClick}
            className={`sub-menu ${directionClass}`}
          />
        ))}
      </div>
    );
  };

  renderSideNavMenu = (items, href, selectedItem = {}, enableCurrencySelector, active = true, withGeoFilter = true) => (
    <SlideWrapper className="mh__nav-dropdown-container" placement="left" fixed shown={active}>
      <div className="sh-iconset sh-iconset-close mh__nav-menu__close-icon" onClick={this.onClickOutside} />
      <div className="mh__nav-menu-modal">
        <div className={`mh__nav-menu-modal__heading ${!selectedItem.id ? 'active' : 'inactive'}`}>
          <Logo
            type={LOGO_TYPES.MERCH}
            brand={this.useAltBrand ? LOGO_BRANDS.ALTERNATIVE : LOGO_BRANDS.DEFAULT}
            onItemClick={this.onItemClick}
          />
          {withGeoFilter && <GeoFilter className="merch-geo-filter--dropdown" />}
        </div>
        <div className={`mh__nav-menu-options main ${!selectedItem.id ? 'active' : 'inactive'}`}>
          {enableCurrencySelector && <LocalizationPicker />}
          {this.exposeExpandedNavMenu ? this.renderHorizontalMenu(items, href) : this.renderVerticalMenu(items, href)}
        </div>
        {this.exposeExpandedNavMenu && this.renderLayeredMenu(items, selectedItem)}
      </div>
    </SlideWrapper>
  );

  render() {
    const {label, href, items, id, subMenu, type, noFormat, screenSize, target, buttonWrapper, withGeoFilter} =
      this.props;
    let {active, activeMenuItem} = this.state;
    const {selectedItem, activeItem} = this.state;
    const subMenus = this.exposeExpandedNavMenu;
    const debug = this.debugSubMenuNav;

    if (debug) {
      active = true; // OJO Debugging purposes only
      activeMenuItem = id; // OJO Debugging purposes only
    }

    const hasItems = items && items.length;
    const headerCls = `mh__nav-menu-header ${active ? 'mh__nav-menu-header--active' : ''} ${
      hasItems ? 'mh__nav-menu-header--has-children' : ''
    }`;
    let dropdown = null;
    let linkIcon = null;
    let linkText = null;
    let mask = null;
    const {globalRegistry} = this.context;
    const enableCurrencySelector = globalRegistry && globalRegistry.getFeatureValue('common.enableAdvisoryCurrency');
    if (screenSize === 'small') {
      if (active) {
        // Render nav menu using JS when toggle is turned off
        if (!this.exposeNavHTML && items && items.length) {
          dropdown = this.renderSideNavMenu(items, href, selectedItem, enableCurrencySelector, true, withGeoFilter);
          // Show a mask only for small screen sizes
          mask = (
            <div>
              <ModalMaskCmp extraCls="mh__nav-menu-mask--background" onClick={this.onClickOutside} />
            </div>
          );
        }
      }

      // Render nav menu using CSS class when toggle is turned on
      if (this.exposeNavHTML && items && items.length) {
        if (id === type) {
          dropdown = this.renderSideNavMenu(items, href, selectedItem, enableCurrencySelector, active, withGeoFilter);
          // Show a mask only for small screen sizes
          mask = (
            <div>
              <ModalMaskCmp
                extraCls={`mh__nav-menu-mask--background ${active ? '' : ' mh__nav-menu-mask--background--hidden'}`}
                onClick={this.onClickOutside}
              />
            </div>
          );
        }
      }
    } else if (subMenus || active) {
      if (items && items.length) {
        const navMenuOptions = (
          <NavMenuOptionList
            items={items}
            href={href}
            onOptionClick={this.onOptionClick}
            onClickOutside={this.onClickOutside}
          />
        );

        dropdown = subMenus ? (
          <div className={`mh__nav-menu-options ${activeMenuItem === id ? '' : 'mh__nav-menu--inactive'}`}>
            {navMenuOptions}
          </div>
        ) : (
          <div className="mh__nav-menu-options">{navMenuOptions}</div>
        );
      }

      // Dropdown for sub category navigation when user hovers over genre category options
      const hasSubCategoryOptions = ['concerts', 'sports', 'theater-comedy', 'cities', 'vip'];

      if (this.enableSubCatNav && id && hasSubCategoryOptions.indexOf(id) + 1) {
        dropdown = subMenus ? (
          <div className={`${activeMenuItem === id ? '' : 'mh__nav-menu--inactive'}`}>
            <NavSubCatList
              id={id}
              menu={subMenu}
              active={active}
              activeItem={activeItem}
              depth={0}
              onMenuHover={this.onMenuHover}
              onOptionClick={this.onOptionClick}
              onClickOutside={this.onClickOutside}
              subMenus={subMenus}
              debug={debug}
            />
          </div>
        ) : (
          <NavSubCatList
            id={id}
            onOptionClick={this.onOptionClick}
            onClickOutside={this.onClickOutside}
            subMenus={subMenus}
            debug={debug}
          />
        );
      }
    } else if (this.exposeNavHTML) {
      dropdown = (
        <div className={`${activeMenuItem === id ? '' : 'mh__nav-menu--inactive'}`}>
          <NavSubCatList id={id} onOptionClick={this.onOptionClick} subMenus={subMenus} debug={debug} />
        </div>
      );
    }

    linkIcon = <span className={`mh__nav-menu-icon menu__icon-${id} ${active ? 'mh__nav-menu-icon--active' : ''}`} />;

    linkText = <span className={`mh__nav-menu-header-text menu__header-text-${id}`}>{label}</span>;

    const navMenuClass = subMenus ? 'mh__nav-menu' : `mh__nav-menu ${active ? 'mh__nav-menu--active' : ''}`;
    const link = (
      <FormattedLink className={headerCls} href={href} onClick={this.onItemClick} aria-label={label} target={target}>
        {linkText}
        {linkIcon}
      </FormattedLink>
    );

    return (
      <div className={navMenuClass} id={id} onMouseOver={this.onItemHover}>
        {noFormat ? (
          <a className={headerCls} href={href} onClick={this.onItemClick} target={target}>
            {linkText}
            {linkIcon}
          </a>
        ) : buttonWrapper ? (
          <Button appearance={id === 'sell' ? 'primary' : 'secondary'}>{link}</Button>
        ) : (
          link
        )}
        {dropdown}
        {mask}
      </div>
    );
  }
}

NavMenu.propTypes = {
  /** Array of menu items */
  items: PropTypes.array,

  /** Menu item label */
  label: PropTypes.string,

  /** Whether the menu is active */
  active: PropTypes.bool,

  /** Active menu item */
  activeMenuItem: PropTypes.string,

  /** Menu item href */
  href: PropTypes.string,

  /** Menu item id */
  id: PropTypes.string,

  /** Callback function when a menu item is clicked */
  onItemClick: PropTypes.func,

  /** Callback function when a menu option is clicked */
  onOptionClick: PropTypes.func,

  /** Callback function when clicked outside of the menu */
  onClickOutside: PropTypes.func,

  /** Callback function when user hovers over a menu option */
  onMenuHover: PropTypes.func,

  /** Callback function when user hovers over a category */
  onCategoryHover: PropTypes.func,

  /** screen size */
  screenSize: PropTypes.string,

  /** target element */
  target: PropTypes.string,

  /** Whether to wrap the link in a button */
  buttonWrapper: PropTypes.bool,

  /** Menu item type */
  type: PropTypes.string,

  /** Sub menu items */
  subMenu: PropTypes.array,

  /** Whether to format the menu item */
  noFormat: PropTypes.bool,

  /** Multi-layered menu items */
  multiLayeredMenu: PropTypes.object,

  /** Web URI */
  webURI: PropTypes.string,

  /** Whether to show the geo filter */
  withGeoFilter: PropTypes.bool,
};

NavMenu.defaultProps = {
  active: false,
  screenSize: 'large',
};

export default connect(controller)(NavMenu);
